import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { ProductListItem } from '@nucleus-commerce/api-client/admin';

import { emptyScheduleForm, toCreateSchedule } from '../api/mappers';
import { PoolPickerSection } from '../components/pool-picker-section';
import { ScheduleForm } from '../components/schedule-form';
import { scheduleKeys } from '../hooks/schedule-keys';
import { lookupsInCache } from '../hooks/use-products-by-ids';
import { useCreateSchedule } from '../hooks/use-schedule-mutations';
import { useStoreOptions } from '../hooks/use-store-options';
import { useNotAddedStore } from '../lib/not-added-store';

const ROUTE_ID = '/_authed/_active/org/schedules/new' as const;

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

function ScheduleCreate() {
  const { t } = useTranslation('schedules');
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const create = useCreateSchedule();
  const stores = useStoreOptions();
  const queryClient = useQueryClient();
  const handOverNotAdded = useNotAddedStore((state) => state.set);
  const handedOver = search.productIds;
  const defaults = useMemo(
    () =>
      emptyScheduleForm({
        timeZone: stores[0]?.timezone ?? browserZone(),
        productIds: handedOver ?? [],
      }),
    // Seeded once: later session refreshes must not reset what has been typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <PageContent>
      <PageHeader
        title={t('create.title')}
        description={
          handedOver && handedOver.length > 0
            ? t('create.handedOver', { count: handedOver.length })
            : t('create.description')
        }
        backTo={{ to: '/org/schedules' }}
        backLabel={t('title')}
        tour="schedules.new.header"
      />
      <ScheduleForm
        mode="create"
        defaultValues={defaults}
        stores={stores}
        isSubmitting={create.isPending}
        onSubmit={async (values, kindOf, intent) => {
          const { schedule, notAdded } = await create.mutateAsync(toCreateSchedule(values, kindOf));
          // Pinned products go in a later call; whatever it left out is listed on the detail
          // page, with the reason, instead of a toast that is gone at once.
          if (notAdded.length > 0) {
            // Handed-over ids got their titles from the batched lookups the pool list ran.
            const looked = lookupsInCache(
              queryClient.getQueriesData<ProductListItem[]>({
                queryKey: scheduleKeys.productLookups(),
              }),
            );
            handOverNotAdded(schedule.id, {
              items: notAdded,
              titles: Object.fromEntries(
                values.products.flatMap((p) => {
                  const title = p.title ?? looked.get(p.id)?.title;
                  return title ? [[p.id, title] as const] : [];
                }),
              ),
            });
          }
          await navigate({
            to: '/org/schedules/$scheduleId',
            params: { scheduleId: schedule.id },
            search: intent === 'preview' ? { preview: true } : {},
            replace: true,
          });
        }}
      >
        <PoolPickerSection />
      </ScheduleForm>
    </PageContent>
  );
}

export function ScheduleCreatePage() {
  return (
    <FeatureErrorBoundary name="schedules.new">
      <ScheduleCreate />
    </FeatureErrorBoundary>
  );
}
